import React from 'react';
import cn from '@/utils/cn';
import Popover from '@/components/ui/Popover';
import type { EndReason, HeartbeatStatus, ProgramOption } from './types';

const HEARTBEAT_OPTIONS: Array<{ label: string; value: HeartbeatStatus }> = [
  { label: 'Healthy', value: 'healthy' },
  { label: 'Stale', value: 'stale' },
  { label: 'Abandoned', value: 'abandoned' },
  { label: 'Missing', value: 'missing' },
];

const END_REASON_OPTIONS: Array<{ label: string; value: EndReason }> = [
  { label: 'Teacher ended', value: 'teacher_ended' },
  { label: 'Timeout', value: 'timeout' },
  { label: 'Error', value: 'error' },
];

const WITHIN_HOURS_OPTIONS: Array<{ hours: number; label: string }> = [
  { hours: 1, label: 'Last hour' },
  { hours: 6, label: 'Last 6 hours' },
  { hours: 24, label: 'Last 24 hours' },
  { hours: 72, label: 'Last 3 days' },
  { hours: 168, label: 'Last 7 days' },
];

const TRIGGER_CLASS = cn(
  'tw-inline-flex tw-items-center tw-gap-2 tw-rounded tw-border tw-border-solid tw-border-gray-300',
  'tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-gray-700 tw-cursor-pointer',
  'hover:tw-bg-gray-50',
  'data-[state=open]:tw-border-tci-purple data-[state=open]:tw-text-gray-900',
);

const BADGE_CLASS = cn(
  'tw-inline-flex tw-items-center tw-justify-center',
  'tw-min-w-[1.25rem] tw-h-5 tw-rounded-full tw-bg-tci-purple tw-text-white tw-text-xs tw-font-semibold tw-px-1.5',
);

const SECTION_LABEL_CLASS = 'tw-text-xs tw-font-semibold tw-uppercase tw-text-gray-500 tw-mb-2';

const CHECKBOX_ROW_CLASS = cn(
  'tw-flex tw-items-center tw-gap-2 tw-py-1 tw-text-sm tw-text-gray-800 tw-cursor-pointer',
);

interface CheckboxOption<T> {
  label: React.ReactNode;
  value: T;
}

interface CheckboxGroupProps<T extends string | number> {
  label: string;
  onChange: (next: T[]) => void;
  options: Array<CheckboxOption<T>>;
  selected: T[];
}

const CheckboxGroup = <T extends string | number>({
  label, onChange, options, selected,
}: CheckboxGroupProps<T>) => {
  const toggle = (value: T) => {
    onChange(selected.includes(value)
      ? selected.filter(v => v !== value)
      : [...selected, value]);
  };

  return (
    <div>
      <div className={SECTION_LABEL_CLASS}>{label}</div>
      <div className="tw-flex tw-flex-col tw-gap-0.5">
        {options.map(opt => (
          <label key={String(opt.value)} className={CHECKBOX_ROW_CLASS}>
            <input
              checked={selected.includes(opt.value)}
              className="tw-h-4 tw-w-4 tw-rounded tw-border-gray-300 tw-mt-0.5 tw-shrink-0"
              type="checkbox"
              onChange={() => toggle(opt.value)}
            />
            <span className="tw-flex-1">{opt.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

// AIDEV-NOTE: Programs share titles ("Social Studies Alive!") across grades/editions, so we
// render Title — Subtitle plus a muted secondary line with Grade + #ID. The #ID is intentional
// for sysadmins/devs who need to disambiguate visually identical entries.
const ProgramOptionLabel = ({ program }: { program: ProgramOption }) => {
  const main = program.subtitle ? `${program.title} — ${program.subtitle}` : program.title;
  const secondary = [
    program.grade ? `Grade ${program.grade}` : null,
    `#${program.id}`,
  ].filter(Boolean).join(' · ');

  return (
    <span className="tw-flex tw-flex-col tw-leading-tight">
      <span className="tw-text-sm tw-text-gray-800">{main}</span>
      <span className="tw-text-xs tw-text-gray-500">{secondary}</span>
    </span>
  );
};

const buildProgramOptions = (programs: ProgramOption[]): Array<CheckboxOption<number>> => (
  programs.map(p => ({ label: <ProgramOptionLabel program={p} />, value: p.id }))
);

interface RadioGroupProps<T> {
  label: string;
  name: string;
  onChange: (value: T) => void;
  options: Array<{ label: string; value: T }>;
  selected: T;
}

const RadioGroup = <T extends string | number>({
  label, name, onChange, options, selected,
}: RadioGroupProps<T>) => (
  <div>
    <div className={SECTION_LABEL_CLASS}>{label}</div>
    <div className="tw-flex tw-flex-col tw-gap-0.5">
      {options.map(opt => (
        <label key={String(opt.value)} className={CHECKBOX_ROW_CLASS}>
          <input
            checked={selected === opt.value}
            className="tw-h-4 tw-w-4 tw-border-gray-300"
            name={name}
            type="radio"
            onChange={() => onChange(opt.value)}
          />
          <span>{opt.label}</span>
        </label>
      ))}
    </div>
  </div>
  );

type TriggerProps = {
  count: number;
  label?: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const FiltersTrigger = React.forwardRef<HTMLButtonElement, TriggerProps>(({
  count, label = 'Filters', ...props
}, ref) => (
  <button ref={ref} className={TRIGGER_CLASS} type="button" {...props}>
    <i aria-hidden className="fa fa-filter tw-text-xs" />
    <span>{label}</span>
    {count > 0 && <span className={BADGE_CLASS}>{count}</span>}
  </button>
));
FiltersTrigger.displayName = 'FiltersTrigger';

interface FooterActionsProps {
  disabled: boolean;
  onClearAll: () => void;
}

const FOOTER_CLASS = cn(
  'tw-flex tw-items-center tw-justify-end tw-px-4 tw-py-2',
  'tw-border-t tw-border-solid tw-border-gray-100',
);

const FooterActions = ({ disabled, onClearAll }: FooterActionsProps) => (
  <div className={FOOTER_CLASS}>
    <button
      className={cn(
        'tw-text-sm tw-text-tci-purple tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-1',
        'hover:tw-underline',
        disabled && 'tw-opacity-40 tw-cursor-not-allowed hover:tw-no-underline',
      )}
      disabled={disabled}
      type="button"
      onClick={onClearAll}
    >
      Clear all
    </button>
  </div>
);

// AIDEV-NOTE: Cap the popover at the Radix-reported "available" height (trigger -> viewport
// edge) so it never spills off-screen. The filter groups scroll; Clear all stays pinned.
const PANEL_STYLE = { maxHeight: 'var(--radix-popover-content-available-height)' } as const;
const PANEL_CONTAINER_CLASS = 'tw-flex tw-flex-col tw-min-w-[16rem] tw-max-w-[20rem]';
const SCROLL_AREA_CLASS = 'tw-flex tw-flex-col tw-gap-4 tw-p-4 tw-flex-1 tw-overflow-y-auto';

interface FiltersPanelProps {
  children: React.ReactNode;
  disabled: boolean;
  onClearAll: () => void;
}

const FiltersPanel = ({ children, disabled, onClearAll }: FiltersPanelProps) => (
  <div className={PANEL_CONTAINER_CLASS} style={PANEL_STYLE}>
    <div className={SCROLL_AREA_CLASS}>{children}</div>
    <FooterActions disabled={disabled} onClearAll={onClearAll} />
  </div>
);

interface ActiveFiltersPopoverProps {
  filterCount: number;
  heartbeat: HeartbeatStatus[];
  onChangeHeartbeat: (next: HeartbeatStatus[]) => void;
  onChangeProgramIds: (next: number[]) => void;
  onClearAll: () => void;
  programIds: number[];
  programs: ProgramOption[];
}

export const ActiveFiltersPopover = ({
  filterCount, heartbeat, onChangeHeartbeat, onChangeProgramIds, onClearAll, programIds, programs,
}: ActiveFiltersPopoverProps) => (
  <Popover>
    <Popover.Trigger asChild>
      <FiltersTrigger count={filterCount} />
    </Popover.Trigger>
    <Popover.Content align="end" collisionPadding={16} side="bottom" width="unset">
      <FiltersPanel disabled={filterCount === 0} onClearAll={onClearAll}>
        <CheckboxGroup
          label="Heartbeat"
          options={HEARTBEAT_OPTIONS}
          selected={heartbeat}
          onChange={onChangeHeartbeat}
        />
        {programs.length > 0 && (
          <CheckboxGroup
            label="Program"
            options={buildProgramOptions(programs)}
            selected={programIds}
            onChange={onChangeProgramIds}
          />
        )}
      </FiltersPanel>
    </Popover.Content>
  </Popover>
);

interface EndedFiltersPopoverProps {
  endReason: EndReason[];
  filterCount: number;
  onChangeEndReason: (next: EndReason[]) => void;
  onChangeProgramIds: (next: number[]) => void;
  onChangeWithinHours: (hours: number) => void;
  onClearAll: () => void;
  programIds: number[];
  programs: ProgramOption[];
  withinHours: number;
}

export const EndedFiltersPopover = ({
  endReason, filterCount, onChangeEndReason, onChangeProgramIds, onChangeWithinHours,
  onClearAll, programIds, programs, withinHours,
}: EndedFiltersPopoverProps) => (
  <Popover>
    <Popover.Trigger asChild>
      <FiltersTrigger count={filterCount} />
    </Popover.Trigger>
    <Popover.Content align="end" collisionPadding={16} side="bottom" width="unset">
      <FiltersPanel disabled={filterCount === 0} onClearAll={onClearAll}>
        <RadioGroup
          label="Time window"
          name="within-hours"
          options={WITHIN_HOURS_OPTIONS.map(o => ({ label: o.label, value: o.hours }))}
          selected={withinHours}
          onChange={onChangeWithinHours}
        />
        <CheckboxGroup
          label="End reason"
          options={END_REASON_OPTIONS}
          selected={endReason}
          onChange={onChangeEndReason}
        />
        {programs.length > 0 && (
          <CheckboxGroup
            label="Program"
            options={buildProgramOptions(programs)}
            selected={programIds}
            onChange={onChangeProgramIds}
          />
        )}
      </FiltersPanel>
    </Popover.Content>
  </Popover>
);
